import { Configuration } from '@/castapi/configuration';
import { apiConfig } from '@/utils/constants';
import type { AddressDto, CountryDto, HttpExceptionDto} from '@/castapi/api';
import {
  AddressApiFactory,
  AwsApiFactory,
  CloudApiFactory,
  DemoApiFactory,
  DictionaryApi,
  DiscountsApiFactory,
  DonglesApiFactory,
  DongleUtilsApiFactory,
  DownloadsApiFactory,
  EmailTemplatesApiFactory,
  SchoolsApiFactory,
  InvoicesApiFactory,
  MajorsApiFactory,
  OrdersApiFactory,
  OrganizationsApiFactory,
  PaymentMethodsApiFactory,
  ProductsApiFactory,
  ReleasesApiFactory,
  ShareFileApiFactory,
  SmsApiFactory,
  SteppingsApiFactory,
  StripeApiFactory,
  SupportRequestApiFactory,
  UploadsApiFactory,
  UsersApiFactory,
  EducationalApiFactory,
} from '@/castapi/api';
import { displayingAddressKeys } from '@/utils/constants/addressKeys';
import { omit } from '@/utils';

export const prepareToServer = <T extends { createdAt?: string }>(value: T): T => {
  const correctedValue = { ...value };
  delete correctedValue.createdAt;
  return correctedValue;
};

export type UserDetailsDrawerData = {
  mainInfo: boolean;
  upgradeCode: boolean;
  subscriptionCode: boolean;
};

export const getErrorMessage = (error: Error | HttpExceptionDto): string => {
  if (!error) {
    return 'Unknown error';
  }
  if (error instanceof Error) {
    return (error as Error).message;
  }
  if (error.status === 500) {
    return 'Something went wrong, try again later';
  }
  return error.data.message;
};

export const isKnownError = (httpException: HttpExceptionDto, code: number, pattern: string | RegExp): boolean => {
  if (httpException?.status !== code) {
    return false;
  }
  if (typeof pattern === 'string') {
    return httpException.data.message === pattern;
  }
  return (pattern as RegExp).test(httpException.data.message);
};

export const getOrdersApi = (accessToken?: string): ReturnType<typeof OrdersApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return OrdersApiFactory(config);
};

export const getProductsApi = (accessToken?: string): ReturnType<typeof ProductsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return ProductsApiFactory(config);
};

export const getDongleUtilitiesApi = (accessToken?: string): ReturnType<typeof DongleUtilsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return DongleUtilsApiFactory(config);
};

export const getStripeApi = (accessToken?: string): ReturnType<typeof StripeApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return StripeApiFactory(config);
};

export const getDiscountApi = (accessToken?: string): ReturnType<typeof DiscountsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return DiscountsApiFactory(config);
};

export const getSteppingsApi = (accessToken?: string): ReturnType<typeof SteppingsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return SteppingsApiFactory(config);
};

export const getDonglesApi = (accessToken?: string): ReturnType<typeof DonglesApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return DonglesApiFactory(config);
};

export const getInvoicesApi = (accessToken?: string): ReturnType<typeof InvoicesApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return InvoicesApiFactory(config);
};

export const getReleasesApi = (accessToken?: string): ReturnType<typeof ReleasesApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return ReleasesApiFactory(config);
};

export const getUploadsApi = (accessToken?: string): ReturnType<typeof UploadsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return UploadsApiFactory(config);
};

export const getEmailTemplatesApi = (accessToken?: string): ReturnType<typeof EmailTemplatesApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return EmailTemplatesApiFactory(config);
};

export const getCloudApi = (accessToken?: string): ReturnType<typeof CloudApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return CloudApiFactory(config);
};

export const getDownloadsApi = (accessToken?: string): ReturnType<typeof DownloadsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return DownloadsApiFactory(config);
};

export const getDemosApi = (accessToken?: string): ReturnType<typeof DemoApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return DemoApiFactory(config);
};

export const getDictionaryApi = (accessToken?: string): DictionaryApi => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return new DictionaryApi(config);
};

export const getOrganizationsApi = (accessToken?: string): ReturnType<typeof OrganizationsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return OrganizationsApiFactory(config);
};

export const getUsersApi = (accessToken?: string): ReturnType<typeof UsersApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return UsersApiFactory(config);
};

export const getAddressApi = (accessToken?: string): ReturnType<typeof AddressApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return AddressApiFactory(config);
};

export const getAwsApi = (accessToken?: string): ReturnType<typeof AwsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return AwsApiFactory(config);
};

export const getSupportRequestApi = (accessToken?: string): ReturnType<typeof SupportRequestApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return SupportRequestApiFactory(config);
};

export const getSharedFileApi = (accessToken?: string): ReturnType<typeof ShareFileApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return ShareFileApiFactory(config);
};

export const getPaymentMethodsApi = (accessToken?: string): ReturnType<typeof PaymentMethodsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return PaymentMethodsApiFactory(config);
};

export const getSmsApi = (accessToken?: string): ReturnType<typeof SmsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return SmsApiFactory(config);
};

export const addressLine = (address: Partial<AddressDto>, countries: CountryDto[]): string =>
  displayingAddressKeys
    .filter(key => address[key])
    .map(key => (key === 'countryRef' ? countries.find(country => country.id === address[key])?.name : address[key]))
    .join(', ');

export const isAddressValid = function (address?: AddressDto): boolean {
  return address
    ? displayingAddressKeys.reduce((acc, curr) => acc && (curr === 'apt' || Boolean(address[curr])), true)
    : true;
};

export const getAddress = (address?: AddressDto): undefined | Partial<AddressDto> =>
  address ? displayingAddressKeys.reduce((acc, curr) => ({ ...acc, [curr]: address[curr] }), {}) : undefined;

export const omitUserSecretFields = <T extends { password?: string; token?: string; recaptchaToken?: string }>(
  user: T,
): Partial<T> => {
  const fieldsToOmit = ['password', 'token', 'recaptchaToken'];
  return omit(user, fieldsToOmit);
};

export interface ISearchParams {
  limit?: number;
  offset?: number;
  searchText?: string;
  sortBy: string;
  sortDesc: boolean;
  includeOrganizationId?: number;
}

export interface IUserSearchParams extends ISearchParams {
  showTeachers?: boolean;
  showStudents?: boolean;
}

export interface IGetPendingApplicantsParams extends ISearchParams {
  showTeachers?: boolean;
  showStudents?: boolean;
  showPending?: boolean;
  showRejected?: boolean;
}

export const getSchoolsApi = (accessToken?: string): ReturnType<typeof SchoolsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return SchoolsApiFactory(config);
};

export const getMajorsApi = (accessToken?: string): ReturnType<typeof MajorsApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return MajorsApiFactory(config);
};

export const getEducationalApi = (accessToken?: string): ReturnType<typeof EducationalApiFactory> => {
  const config = new Configuration({
    basePath: apiConfig.basePath,
  });
  if (accessToken) {
    config.accessToken = accessToken;
  }
  return EducationalApiFactory(config);
};
